import type {
  SectionValue,
  KeyImprovementsSection as KeyImprovementsData,
} from "../../../../shared/analysis-types";
import { SectionHeader } from "./section-header";
import { NarrativeCard, NarrativeCardLede } from "./narrative-card";
import { LabeledBody } from "./labeled-body";
import { SectionErrorFallback } from "./section-error-fallback";
import { CopyFormattedButton } from "@/components/copy-formatted-button";
import {
  formatKeyImprovementCopy,
  formatKeyImprovementsCopy,
} from "@/lib/analysis-copy-format";

interface KeyImprovementsSectionProps {
  data: SectionValue<KeyImprovementsData>;
  className?: string;
}

export function KeyImprovementsSection({
  data,
  className,
}: KeyImprovementsSectionProps) {
  if ("error" in data) {
    return (
      <div className={className}>
        <SectionHeader title="Key Improvements" />
        <div className="mt-4">
          <SectionErrorFallback sectionName="key_improvements" />
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <SectionHeader
        title="Key Improvements"
        count={data.improvements.length}
        action={
          <CopyFormattedButton format={formatKeyImprovementsCopy(data)}>
            Copy section
          </CopyFormattedButton>
        }
      />
      <div className="mt-4 flex flex-col gap-6">
        {data.improvements.map((imp, i) => (
          <NarrativeCard key={i} className="group relative pt-14 sm:pt-5 sm:pr-32">
            <CopyFormattedButton
              format={formatKeyImprovementCopy(imp)}
              className="absolute right-5 top-5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
            >
              Copy item
            </CopyFormattedButton>
            <NarrativeCardLede>{imp.lede}</NarrativeCardLede>

            <LabeledBody label="Where" variant="descriptive">
              {imp.where}
            </LabeledBody>

            <LabeledBody label="Why it matters" variant="descriptive">
              {imp.why_it_matters}
            </LabeledBody>

            <LabeledBody label="Try this" variant="action">
              {imp.try_this}
            </LabeledBody>
          </NarrativeCard>
        ))}
      </div>
    </div>
  );
}
